import { UnwrapPromise } from "@prisma/client";
import reachDialogStyles from "@reach/dialog/styles.css";
import reachTooltipStyles from "@reach/tooltip/styles.css";
import { CatchBoundaryComponent } from "@remix-run/react/routeModules";
import { IKContext } from "imagekitio-react";
import { PropsWithChildren, useEffect } from "react";
import {
  ErrorBoundaryComponent,
  HeadersFunction,
  json,
  Link,
  Links,
  LinksFunction,
  LiveReload,
  LoaderFunction,
  Meta,
  MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  useCatch,
  useLoaderData,
  useLocation,
} from "remix";
import { Breadcrumbs } from "./components/Breadcrumbs";
import { ButtonLink } from "./components/Button";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header";
import { Navigation } from "./components/Navigation";
import { Toast, Toaster } from "./components/Toast";
import { getUploadDialogConfig } from "./components/UploadDialog";
import styles from "./styles/app.css";
import toasterStyles from "./styles/toaster.css";
import { ensureHttps } from "./utils/ensureHttps";
import {
  defaultMainColor,
  defaultTheme,
  getCurrentFestival,
  getFestivalThemes,
} from "./utils/festival.server";
import { imagekit } from "./utils/imageKit.server";
import {
  getUser,
  getUserSession,
  sessionStorage,
} from "./utils/session.server";
import { useFestival } from "./utils/useFestival";

export const links: LinksFunction = () => {
  return [
    {
      rel: "preconnect",
      href: "https://fonts.googleapis.com",
    },
    {
      rel: "preconnect",
      href: "https://fonts.gstatic.com",
      crossOrigin: "anonymous",
    },
    {
      href: "https://fonts.googleapis.com/css2?family=Montserrat:wght@500;700&display=swap",
      rel: "stylesheet",
    },

    { rel: "stylesheet", href: styles },
    { rel: "stylesheet", href: toasterStyles },
    { rel: "stylesheet", href: reachDialogStyles },
    { rel: "stylesheet", href: reachTooltipStyles },
  ];
};

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  return {
    // "WWW-Authenticate": "Basic",
  };
};

export const meta: MetaFunction = ({ location, data }) => {
  const mainColor = data?.mainColor ?? defaultMainColor;
  return {
    title: "We Won't Shut Up!",
    description: "Hybride Aktionstage zum Internationalen Frauentag",
    "og:image": data?.socialImage ?? undefined,
    "og:url": `https://wewontshutup.org${location.pathname}`,
    "og:type": "website",
    "og:title": "We Won't Shut Up!",
    "og:description": "Hybride Aktionstage zum Internationalen Frauentag",
    "msapplication-TileColor": mainColor,
    "theme-color": mainColor,
  };
};

interface LoaderData {
  username?: string;
  flashMessage?: string | null;
  festivalTheme: UnwrapPromise<ReturnType<typeof getFestivalThemes>>;
  mainColor: string;
  socialImage: string | null;
  currentFestivalId?: string;
}

// function isAuthorized(request: Request) {
//   const header = request.headers.get("Authorization");

//   if (!header) return false;

//   const base64 = header.replace("Basic ", "");
//   const [username, password] = Buffer.from(base64, "base64")
//     .toString()
//     .split(":");

//   return username === "admin" && password === "admin";
// }

export const loader: LoaderFunction = async ({ request, params }) => {
  ensureHttps(request);

  const [festivalTheme, currentFestival, user, session] = await Promise.all([
    getFestivalThemes(),
    getCurrentFestival(),
    getUser(request),
    getUserSession(request),
  ]);

  const mainColor = currentFestival?.color600 ?? defaultMainColor;
  const flashMessage = session.get("message") || null;

  const uploadConfig = !!user ? await getUploadDialogConfig(request) : null;

  const socialImagePath = currentFestival?.socialImage?.url;
  const socialImage = socialImagePath
    ? imagekit.url({ path: socialImagePath })
    : undefined;

  return json(
    {
      username: user?.username,
      isLoggedIn: !!user,
      userRole: user?.role,
      socialImage,
      uploadConfig,
      festivalTheme,
      mainColor,
      currentFestivalId: currentFestival?.id,
      flashMessage,
    },
    {
      headers: flashMessage
        ? {
            "Set-Cookie": await sessionStorage.commitSession(session),
          }
        : undefined,
    }
  );
};

function Document({
  children,
  flashMessage,
  festivalTheme,
  currentFestivalId,
}: PropsWithChildren<Omit<LoaderData, "socialImage">>) {
  const location = useLocation();

  const festival = useFestival();

  useEffect(() => {
    let themeIdToUse =
      festival?.id ?? document.documentElement.dataset.currentFestival;

    if (location.pathname.startsWith("/admin")) {
      themeIdToUse = undefined;
    }

    document.documentElement.dataset.theme = themeIdToUse;
  }, [festival?.id, location.pathname]);

  return (
    <html
      lang="en"
      data-current-festival={currentFestivalId}
      data-theme={currentFestivalId}
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
        <style
          dangerouslySetInnerHTML={{
            __html: festivalTheme,
          }}
        ></style>
      </head>
      <IKContext urlEndpoint={`https://ik.imagekit.io/wdljk7lxz5j/`}>
        <body>
          <Toaster>
            {flashMessage && <Toast key="flash">{flashMessage}</Toast>}
          </Toaster>
          {location.pathname !== "/" && <Header />}
          <main>
            <Navigation />
            <Breadcrumbs />
            {children}
          </main>
          <Footer />
          <ScrollRestoration />
          <Scripts />
          {process.env.NODE_ENV === "development" && <LiveReload />}
          <script
            data-no-cookie
            data-respect-dnt
            async
            src="https://cdn.splitbee.io/sb.js"
          ></script>
        </body>
      </IKContext>
    </html>
  );
}

export default function App() {
  const {
    username,
    currentFestivalId,
    festivalTheme,
    mainColor,
    flashMessage,
  } = useLoaderData<LoaderData>();

  return (
    <Document
      currentFestivalId={currentFestivalId}
      username={username}
      flashMessage={flashMessage}
      festivalTheme={festivalTheme}
      mainColor={mainColor}
    >
      <Outlet />
    </Document>
  );
}

export const CatchBoundary: CatchBoundaryComponent = ({ ...info }) => {
  const err = useCatch();

  return (
    <Document festivalTheme={defaultTheme} mainColor={defaultMainColor}>
      <div className="main-layout-centered">
        <div className="my-8 prose">
          <h1 className="text-6xl">
            {err.status} - {err.data}
          </h1>
          <p>
            {err.status === 404 &&
              "Die Seite konnte leider nicht gefunden werden."}
            <br />
            <Link to="/">Gehe zurück auf Start</Link>
          </p>
        </div>
      </div>
    </Document>
  );
};

export const ErrorBoundary: ErrorBoundaryComponent = ({ error }) => {
  return (
    <Document festivalTheme={defaultTheme} mainColor={defaultMainColor}>
      <div className="main-layout-centered">
        <div className="my-8 prose">
          <h1 className="text-6xl">500 - Server error</h1>
          <img src="/illustrations/fixing-bugs.svg" className="w-2/3" />
          <p>Sorry, es gab leider einen Fehler.</p>
          <p>
            <ButtonLink to="/">Gehe zurück auf Start</ButtonLink>
          </p>
        </div>
      </div>
    </Document>
  );
};
